<template>
    <div class="contact-popup__root">
        <Transition name="contact-popup-fade">
            <button
                v-if="isOpen"
                type="button"
                class="contact-popup__backdrop"
                aria-label="Sluiten"
                @click="closeModal"
            />
        </Transition>

        <Transition name="contact-popup-trigger">
            <ContactPopupTrigger
                v-if="showTrigger"
                :pulse="pulseActive"
                @open="openModal"
            />
        </Transition>

        <Transition name="contact-popup-panel" @after-leave="onPanelAfterLeave">
            <ContactPopupModal
                v-if="isOpen"
                :loading="loading"
                :submitting="submitting"
                :fields="fields"
                :errors="errors"
                :general-error="generalError"
                :success="success"
                @close="closeModal"
                @submit="handleSubmit"
            />
        </Transition>
    </div>
</template>

<script>
import ContactPopupTrigger from "./components/ContactPopupTrigger.vue";
import ContactPopupModal from "./components/ContactPopupModal.vue";
import { fetchFormDefinition, submitForm } from "./api/contactPopup";
import {
    dismissPulseTriggers,
    setupPulseTriggers,
} from "./pulseTriggers";

export default {
    name: "ContactPopupApp",
    components: {
        ContactPopupTrigger,
        ContactPopupModal,
    },
    props: {
        apiUrl: {
            type: String,
            required: true,
        },
        pageId: {
            type: String,
            default: "",
        },
        pageClass: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isOpen: false,
            showTrigger: true,
            pulseActive: false,
            pulseCleanup: null,
            loading: false,
            submitting: false,
            fields: [],
            submitUrl: "",
            loadPromise: null,
            errors: {},
            generalError: "",
            success: false,
        };
    },
    mounted() {
        const triggers = setupPulseTriggers({
            pageClass: this.pageClass,
            onActivate: () => {
                this.pulseActive = true;
            },
        });

        this.pulseCleanup = triggers.cleanup;
        this.loadForm();
    },
    beforeUnmount() {
        this.pulseCleanup?.();
    },
    methods: {
        openModal() {
            if (this.isOpen) {
                return;
            }

            this.pulseActive = false;
            dismissPulseTriggers(this.pulseCleanup);
            this.pulseCleanup = () => {};

            this.showTrigger = false;
            this.isOpen = true;
            this.success = false;
            this.errors = {};
            this.generalError = "";
            document.body.classList.add("contact-popup--open");
            this.loadForm();
        },
        closeModal() {
            if (!this.isOpen) {
                return;
            }

            this.isOpen = false;
            this.success = false;
            this.errors = {};
            this.generalError = "";
            document.body.classList.remove("contact-popup--open");
        },
        onPanelAfterLeave() {
            if (!this.isOpen) {
                this.showTrigger = true;
            }
        },
        loadForm() {
            if (this.fields.length > 0) {
                return Promise.resolve();
            }

            if (this.loadPromise) {
                return this.loadPromise;
            }

            this.loading = true;

            this.loadPromise = fetchFormDefinition(this.apiUrl)
                .then((data) => {
                    this.fields = data.fields || [];
                    this.submitUrl = data.submit_url || "";
                })
                .catch((error) => {
                    this.loadPromise = null;
                    this.generalError =
                        "Het formulier kon niet worden geladen. Probeer het later opnieuw.";
                    console.error("Contact popup load error", error);
                })
                .finally(() => {
                    this.loading = false;
                });

            return this.loadPromise;
        },
        async handleSubmit(values) {
            this.submitting = true;
            this.errors = {};
            this.generalError = "";

            const payload = { ...values };

            if (this.pageId) {
                payload.page_id = this.pageId;
            }

            try {
                const result = await submitForm(this.submitUrl, payload);

                if (result.ok && result.data.success) {
                    this.success = true;
                    return;
                }

                if (result.status === 422 && result.data.errors) {
                    this.errors = result.data.errors;
                    return;
                }

                this.generalError =
                    result.data.errors?.general ||
                    "Er is een fout opgetreden. Probeer het later opnieuw.";
            } catch (error) {
                this.generalError =
                    "Er is een fout opgetreden. Probeer het later opnieuw.";
                console.error("Contact popup submit error", error);
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
